import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import TuneIcon from "@mui/icons-material/Tune";
import { toast } from "react-toastify";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import {
  getCompanyUploadedFileSignedUrl,
  getPaymentPlatformImport,
  getPaymentPlatformOverview,
  previewAllPaymentPlatformFiles,
  registerPaymentPlatformFile,
  startPaymentPlatformImport,
  updatePaymentPlatformModel,
} from "./api/companyApi";
import { runClaudeChatJob } from "./api/claudeApi";
import PaymentPlatformDecisionStudio from "./components/PaymentPlatformDecisionStudio";
import PaymentPlatformAddCompanyFlow from "./components/PaymentPlatformAddCompanyFlow";
import PaymentPlatformColumnMappingDialog from "./components/PaymentPlatformColumnMappingDialog";
import PaymentPlatformFileUploadDialog from "./components/PaymentPlatformFileUploadDialog";
import PaymentPlatformModelLab from "./components/PaymentPlatformModelLab";
import PaymentPlatformBucketPanel from "./components/PaymentPlatformBucketPanel";
import PaymentPlatformDataCenter from "./components/PaymentPlatformDataCenter";
import { buildPaymentPlatformCopilotContext } from "./utils/paymentPlatformCopilotContext";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#12a150",
  amber: "#d97706",
  red: "#dc2626",
};

const fmt = (value) => (value == null ? "—" : Number(value).toLocaleString());
const money = (value) =>
  value == null ? "—" : `$${Number(value).toLocaleString()}`;
const decisionColor = (value) =>
  value === "approve"
    ? C.green
    : value === "review"
      ? C.amber
      : value === "decline"
        ? C.red
        : C.muted;

export const MCA_MODEL_CHAT_LAYOUT_SX = {
  display: "grid",
  gridTemplateRows: "minmax(0, 1fr) auto",
  height: { xs: "calc(100dvh - 220px)", md: 600 },
  minHeight: { xs: 420, md: 520 },
  maxHeight: { xs: 620, md: "calc(100dvh - 220px)" },
  overflow: "hidden",
};

export const MCA_MODEL_CHAT_HISTORY_SX = {
  p: 2.5,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
};

export const PAYMENT_PLATFORM_WORKSPACE_TAB_LABELS = [
  "Decision studio",
  "Data & matching",
  "Model Lab",
  "ArcAI model chat",
];

function Kpi({ label, value, sub }) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderColor: C.line, borderRadius: 3 }}
    >
      <Typography
        sx={{
          fontSize: 11,
          color: C.muted,
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 25, color: C.ink, fontWeight: 900 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: C.muted }}>{sub}</Typography>
      )}
    </Card>
  );
}

function AssessmentResult({ assessment }) {
  if (!assessment) return null;
  const decision = assessment.assessment?.decision;
  const hasOffer =
    decision === "approve" &&
    assessment.assessment?.recommendation?.amount != null;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: C.line, borderRadius: 3, mb: 2 }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography sx={{ fontWeight: 900, color: C.ink }}>
          New recommendation
        </Typography>
        <Chip
          size="small"
          label={assessment.assessment?.decision?.replaceAll("_", " ")}
          sx={{
            color: decisionColor(assessment.assessment?.decision),
            fontWeight: 800,
          }}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <Kpi label="Score" value={assessment.assessment?.score ?? "—"} />
        <Kpi
          label="Evidence coverage"
          value={`${assessment.assessment?.coverage || 0}%`}
        />
        <Kpi
          label="Offer"
          value={
            hasOffer
              ? money(assessment.assessment?.recommendation?.amount)
              : "No offer"
          }
        />
        <Kpi
          label="Term"
          value={
            hasOffer && assessment.assessment?.recommendation?.termMonths
              ? `${assessment.assessment.recommendation.termMonths} months`
              : "—"
          }
        />
      </Box>
    </Paper>
  );
}

export default function PaymentPlatformAssessmentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setPortfolioCopilotContext } = useOutletContext() || {};
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const tenantId = isAdmin ? getTenantIdFromURL() : currentTenantId;
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedUploadFiles, setSelectedUploadFiles] = useState([]);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [mappingBusy, setMappingBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newAssessment, setNewAssessment] = useState(null);
  const [modelDraft, setModelDraft] = useState(null);
  const [savingModel, setSavingModel] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [launchUpload, setLaunchUpload] = useState(false);
  const [catalogRefreshKey, setCatalogRefreshKey] = useState(0);
  const fileInput = useRef(null);
  const chatScrollRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let consumedAction = false;
    if (params.get("addCompany") === "1") {
      setAddOpen(true);
      params.delete("addCompany");
      consumedAction = true;
    }
    if (params.get("uploadData") === "1") {
      setTab(1);
      setLaunchUpload(true);
      params.delete("uploadData");
      consumedAction = true;
    }
    if (params.get("cloudStorage") === "1") {
      setTab(1);
      params.delete("cloudStorage");
      consumedAction = true;
      window.setTimeout(() => {
        document
          .getElementById("payment-platform-cloud-storage")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
    if (consumedAction) {
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);
  useEffect(() => {
    if (!launchUpload || tab !== 1 || !fileInput.current) return undefined;
    const timer = window.setTimeout(() => {
      fileInput.current?.click();
      setLaunchUpload(false);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [launchUpload, tab]);
  const [chatBusy, setChatBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  useEffect(() => {
    if (tab !== 3 || !chatScrollRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (chatScrollRef.current)
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [chatBusy, messages, proposal, tab]);
  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getPaymentPlatformOverview(tenantId);
      setOverview(data);
      setModelDraft(data?.model || null);
      setJob(data?.latestImport || null);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Could not load payment-platform assessments",
      );
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!setPortfolioCopilotContext || !tenantId) return undefined;
    const data = buildPaymentPlatformCopilotContext(
      overview ? { ...overview, latestImport: job || overview.latestImport } : null
    );
    setPortfolioCopilotContext({ tenantId, data });
    return () => setPortfolioCopilotContext((current) => (
      current?.tenantId === tenantId && current?.data?.workspace === "MCA / Loan Assessments"
        ? null
        : current
    ));
  }, [job, overview, setPortfolioCopilotContext, tenantId]);
  useEffect(() => {
    if (!job?.id || !["queued", "processing"].includes(job.status))
      return undefined;
    const timer = setInterval(async () => {
      try {
        const latest = await getPaymentPlatformImport(tenantId, job.id);
        setJob(latest);
        if (latest.status === "completed") {
          toast.success("Entity matching and MCA / loan assessments completed");
          await load();
        }
        if (latest.status === "failed")
          toast.error(latest.error || "Import failed");
      } catch (error) {
        /* retry on next poll */
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [job?.id, job?.status, tenantId, load]);

  const selectUploadFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    setSelectedUploadFiles(selected);
  };

  const uploadFiles = async (labelledFiles) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const item of labelledFiles) {
        const { file, labels } = item;
        const signed = await getCompanyUploadedFileSignedUrl(tenantId, {
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
        });
        await axios.put(signed.data.uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        const catalogFile = await registerPaymentPlatformFile(tenantId, {
          fileGCSKey: signed.data.fileGCSKey,
          fileName: signed.data.originalFileName || file.name,
          labels,
          source: "dashboard_upload",
        });
        uploaded.push(catalogFile);
      }
      const preview = await previewAllPaymentPlatformFiles(tenantId);
      setPendingUpload({ files: preview.files, preview });
      setSelectedUploadFiles([]);
      setCatalogRefreshKey((value) => value + 1);
      toast.success(`${uploaded.length} file${uploaded.length === 1 ? "" : "s"} added. Map columns across all tenant files.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "File upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startUploadedImport = async (columnMapping) => {
    if (!pendingUpload?.files?.length) return;
    setUploading(true);
    try {
      const started = await startPaymentPlatformImport(
        tenantId,
        pendingUpload.files,
        null,
        columnMapping,
      );
      setJob(started);
      setPendingUpload(null);
      toast.success("Column mapping saved. Assessments are rebuilding with the uploaded data.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not start import");
    } finally {
      setUploading(false);
    }
  };

  const openColumnMapping = async () => {
    setMappingBusy(true);
    try {
      const preview = await previewAllPaymentPlatformFiles(tenantId);
      if (!preview?.files?.length) {
        toast.error("Upload at least one file before mapping columns.");
        return;
      }
      setMappingPreview(preview);
      setMappingOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not inspect import columns");
    } finally {
      setMappingBusy(false);
    }
  };

  const applyExistingColumnMapping = async (columnMapping) => {
    const files = pendingUpload?.files || mappingPreview?.files;
    if (!files?.length) return;
    setMappingBusy(true);
    try {
      const started = await startPaymentPlatformImport(tenantId, files, null, columnMapping);
      setJob(started);
      setPendingUpload(null);
      setMappingOpen(false);
      toast.success("Column mapping saved. Assessments are rebuilding from every file in the data center.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not apply column mapping");
    } finally {
      setMappingBusy(false);
    }
  };

  const progress = job?.matchedEntities
    ? Math.round(((job.assessedEntities || 0) / job.matchedEntities) * 100)
    : job?.filesRead
      ? Math.round((job.filesRead / job.totalFiles) * 25)
      : 0;
  const activeColumnMapping =
    job?.columnMappingDraft ||
    modelDraft?.trainingColumnMappingDraft ||
    job?.columnMapping ||
    modelDraft?.trainingColumnMapping ||
    null;
  const mappedFieldCount = Object.values(activeColumnMapping?.fields || {}).filter(Boolean).length;
  const mappingIsProcessing = ["queued", "processing"].includes(job?.status);
  const updateModel = (section, key, value) =>
    setModelDraft((current) => ({
      ...current,
      [section]: { ...(current?.[section] || {}), [key]: value },
    }));
  const saveModel = async (patch = modelDraft) => {
    setSavingModel(true);
    try {
      const saved = await updatePaymentPlatformModel(tenantId, patch);
      setModelDraft(saved);
      setProposal(null);
      toast.success(
        `Model v${saved.version} saved for ${overview?.tenant?.name}`,
      );
      return saved;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save model");
      return null;
    } finally {
      setSavingModel(false);
    }
  };

  const chatContext = useMemo(
    () => ({
      tenant: overview?.tenant,
      model: modelDraft,
      dataset: job
        ? {
            profiles: job.profiles,
            outcomes: job.outcomes,
            validation: job.validation,
            matchedEntities: job.matchedEntities,
            decisionDistribution: job.decisionDistribution,
          }
        : null,
    }),
    [overview?.tenant, modelDraft, job],
  );
  const askModel = async () => {
    if (!chatInput.trim()) return;
    const nextMessages = [
      ...messages,
      { role: "user", content: chatInput.trim() },
    ];
    setMessages(nextMessages);
    setChatInput("");
    setChatBusy(true);
    try {
      const response = await runClaudeChatJob({
        tenantName: overview?.tenant?.name || "Payment platform",
        entityName: "MCA / loan model configuration",
        entity: {
          ...chatContext,
          paymentPlatformModelTuning: true,
          tenantId,
          instruction:
            'Analyze only schema, coverage, statistics, measured experiments and the draft policy. Never claim the model is validated when outcomes are one-sided. Changes remain proposals until the user explicitly applies them.',
        },
        messages: nextMessages,
        allowWeb: false,
      });
      const reply =
        response?.data?.reply ||
        response?.reply ||
        response?.data ||
        "No response";
      setMessages((current) => [
        ...current,
        { role: "assistant", content: String(reply) },
      ]);
      const match = String(reply).match(/\{\s*"modelPatch"[\s\S]*\}/);
      if (match) {
        try {
          setProposal(JSON.parse(match[0]).modelPatch || null);
        } catch (error) {
          /* prose-only response */
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "ArcAI could not answer");
    } finally {
      setChatBusy(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (!overview)
    return (
      <Alert severity="warning">
        Payment-platform assessment workspace is unavailable.
      </Alert>
    );
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1450, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          alignItems: "flex-start",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 950, color: C.ink }}>
            MCA / Loan Assessments
          </Typography>
          <Typography sx={{ color: C.muted }}>
            Sparse-identity merchant matching and a model isolated to{" "}
            {overview.tenant?.name} data.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddBusinessIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add company
          </Button>
        </Box>
      </Box>
      <Alert
        severity={
          modelDraft?.validation?.status === "validated"
            ? "success"
            : "error"
        }
        sx={{ mb: 2 }}
      >
        <strong>Model validation:</strong>{" "}
        {modelDraft?.validation?.status?.replaceAll("_", " ")} —{" "}
        {modelDraft?.validation?.reason}{" "}
        {modelDraft?.validation?.status !== "validated" &&
          "Outputs are draft policy simulations, require human review, and must not trigger automated funding."}
      </Alert>
      <AssessmentResult assessment={newAssessment} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Kpi
          label="Matched merchants"
          value={fmt(job?.matchedEntities || 0)}
          sub={`${fmt(job?.unmatchedRows || 0)} require identity review`}
        />
        <Kpi
          label={isAdmin ? "Files in latest run" : "Assessed merchants"}
          value={fmt(isAdmin ? (job?.totalFiles || job?.files?.length || 0) : (job?.assessedEntities || 0))}
          sub={isAdmin ? (job?.status || "No import") : "Current decision population"}
        />
        <Kpi
          label="Approve / review"
          value={`${fmt(job?.decisionDistribution?.approve || 0)} / ${fmt(job?.decisionDistribution?.review || 0)}`}
          sub="Policy recommendations"
        />
        <Kpi
          label="Model version"
          value={`v${modelDraft?.version || 1}`}
          sub={modelDraft?.status || "draft"}
        />
      </Box>
      <Paper variant="outlined" sx={{ borderColor: C.line, borderRadius: 3 }}>
        <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ px: 2 }}>
          {PAYMENT_PLATFORM_WORKSPACE_TAB_LABELS.map((label) => (
            <Tab key={label} label={label === "Data & matching" && !isAdmin ? "Data quality" : label} />
          ))}
        </Tabs>
        <Divider />
        {tab === 0 && modelDraft && (
          <PaymentPlatformDecisionStudio
            tenantId={tenantId}
            modelDraft={modelDraft}
            job={job}
            savingModel={savingModel}
            onUpdateModel={updateModel}
            onSaveModel={saveModel}
            onOpenChat={() => setTab(3)}
          />
        )}
        {tab === 1 && (
          <Box sx={{ p: 2.5 }}>
            <input
              hidden
              multiple
              ref={fileInput}
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={selectUploadFiles}
            />
            <PaymentPlatformDataCenter
              tenantId={tenantId}
              refreshKey={catalogRefreshKey}
              busy={uploading || mappingBusy}
              onUpload={() => fileInput.current?.click()}
              onMapColumns={openColumnMapping}
            />
            <PaymentPlatformBucketPanel
              tenantId={tenantId}
              isAdmin={isAdmin}
              onImportQueued={setJob}
              compact
            />
            {job && (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderColor: C.line, mb: 2 }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {job.stage?.replaceAll("_", " ") || job.status}
                  </Typography>
                  <Typography>{progress}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={job.status === "completed" ? 100 : progress}
                  sx={{ mt: 1 }}
                />
                {job.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {job.error}
                  </Alert>
                )}
              </Paper>
            )}
            {job && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: activeColumnMapping ? "#b9dfca" : C.line,
                  bgcolor: activeColumnMapping ? "#f5fcf8" : "#fff",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: C.ink }}>
                      Column mapping & ML readiness
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.25 }}>
                      Map identity, advance size, pricing, model features and the
                      historical outcome. Rebuilding reuses these files.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
                      <Chip
                        size="small"
                        color={mappedFieldCount ? "success" : "default"}
                        label={`${mappedFieldCount} input columns mapped`}
                      />
                      <Chip
                        size="small"
                        color={activeColumnMapping?.label ? "success" : "warning"}
                        label={activeColumnMapping?.label ? "Outcome label mapped" : "Outcome label needed"}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<TuneIcon />}
                    disabled={mappingBusy || mappingIsProcessing}
                    onClick={openColumnMapping}
                  >
                    {mappingBusy ? "Inspecting columns…" : "Configure columns"}
                  </Button>
                </Box>
              </Paper>
            )}
            {Boolean(job?.profiles?.length) && (
              <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 1 }}>
                {job.profiles.length} labelled data source
                {job.profiles.length === 1 ? "" : "s"} included in this run.
              </Typography>
            )}
            {!isAdmin && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
                <Kpi label="Resolved merchants" value={fmt(job?.matchedEntities || 0)} sub="Available for assessment" />
                <Kpi label="Identity review" value={fmt(job?.unmatchedRows || 0)} sub="Needs additional verification" />
                <Kpi label="Assessment coverage" value={`${job?.matchedEntities ? Math.round((Number(job?.assessedEntities || 0) / Number(job.matchedEntities)) * 100) : 0}%`} sub="Current population assessed" />
              </Box>
            )}
          </Box>
        )}
        {tab === 2 && modelDraft && (
          <PaymentPlatformModelLab
            tenantId={tenantId}
            modelDraft={modelDraft}
            job={job}
            onOpenPolicy={() => setTab(0)}
            onOpenChat={() => setTab(3)}
            onOpenData={() => setTab(1)}
          />
        )}
        {tab === 3 && (
          <Box
            data-mca-model-chat
            sx={MCA_MODEL_CHAT_LAYOUT_SX}
          >
            <Box
              ref={chatScrollRef}
              data-mca-model-chat-history
              sx={MCA_MODEL_CHAT_HISTORY_SX}
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                ArcAI can analyze coverage, challenge weights and propose a
                model patch. Changes require an explicit Apply action and remain
                isolated to this tenant.
              </Alert>
              {messages.map((message, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1,
                    ml: message.role === "user" ? 8 : 0,
                    mr: message.role === "assistant" ? 8 : 0,
                    bgcolor: message.role === "user" ? "#edf4ff" : "#fff",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 10, fontWeight: 900, color: C.muted }}
                  >
                    {message.role === "user" ? "YOU" : "ARCAI"}
                  </Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>
                    {message.content}
                  </Typography>
                </Paper>
              ))}
              {proposal && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      size="small"
                      disabled={savingModel}
                      onClick={() =>
                        saveModel({
                          ...modelDraft,
                          ...proposal,
                          thresholds: {
                            ...modelDraft.thresholds,
                            ...proposal.thresholds,
                          },
                          weights: {
                            ...modelDraft.weights,
                            ...proposal.weights,
                          },
                          guardrails: {
                            ...modelDraft.guardrails,
                            ...proposal.guardrails,
                          },
                          offer: { ...modelDraft.offer, ...proposal.offer },
                        })
                      }
                    >
                      Apply proposal
                    </Button>
                  }
                >
                  ArcAI proposed a configuration change. Review it before
                  applying.
                </Alert>
              )}
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderTop: `1px solid ${C.line}`,
                display: "flex",
                gap: 1,
                bgcolor: "#fff",
              }}
            >
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Ask how Liberis data should change the MCA / loan policy…"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    askModel();
                  }
                }}
              />
              <IconButton
                onClick={askModel}
                disabled={chatBusy || !chatInput.trim()}
                sx={{
                  bgcolor: C.blue,
                  color: "#fff",
                  "&:hover": { bgcolor: "#1559be" },
                }}
              >
                {chatBusy ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>
      <PaymentPlatformColumnMappingDialog
        open={Boolean(pendingUpload) || mappingOpen}
        preview={pendingUpload?.preview || mappingPreview}
        currentMapping={pendingUpload ? null : activeColumnMapping}
        busy={uploading || mappingBusy}
        onClose={() => {
          if (pendingUpload) setPendingUpload(null);
          setMappingOpen(false);
        }}
        onApply={pendingUpload ? startUploadedImport : applyExistingColumnMapping}
        onSaveDraft={null}
      />
      <PaymentPlatformFileUploadDialog
        open={selectedUploadFiles.length > 0}
        files={selectedUploadFiles}
        busy={uploading}
        onClose={() => setSelectedUploadFiles([])}
        onUpload={uploadFiles}
      />
      <PaymentPlatformAddCompanyFlow
        tenantId={tenantId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(assessment) => {
          setNewAssessment(assessment);
        }}
        onOpenReport={(entityId) => navigate(`/tenant-dashboard/${tenantId}/assessments/company/${entityId}`)}
      />
    </Box>
  );
}
